// settingsFingerprint — what the client sends with a whole-project save so
// the server writes only what THIS save changed (see the backend mirror,
// backend/utils/settingsFingerprint.js, for the merge rules).
//
// MIRROR of the backend's fingerprint / buildBase — they must compute the same
// fingerprints (settingsFingerprint.eval.js compares them).

const NOT_SETTINGS = new Set([
  'id', 'name', 'description', 'client', 'tags', 'orgId', 'shareWithOrg',
  'createdAt', 'updatedAt', 'createdBy', '_members', 'myRole', 'myRegionAccess', '_base', '_listSlim',
]);

/** Workspace detail columns (not settings), owned by PATCH /details. */
const DETAIL_FIELDS = ['name', 'description', 'client', 'tags'];

const FILE_PROP_FIELDS = [
  'extractedProperties', 'docPropTemplateId', 'docPropIssues', 'docPropTemplateMismatch',
  'docPropAuditedAt', 'docPropExtractionFailed', 'docPropExtractedAt', 'docPropCheck', 'pages',
];

/** FNV-1a over the JSON text, plus its length. 'u' for undefined. */
export function fingerprint(value) {
  if (value === undefined) return 'u';
  const s = JSON.stringify(value);
  if (s === undefined) return 'u';
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return `${(h >>> 0).toString(36)}:${s.length}`;
}

function filePropsOf(f) {
  const o = {};
  for (const k of FILE_PROP_FIELDS) if (f && f[k] !== undefined) o[k] = f[k];
  return o;
}

function eachFile(project, fn) {
  const walk = (folders) => {
    for (const folder of folders || []) {
      for (const f of folder?.files || []) fn(f);
      walk(folder?.subfolders);
    }
  };
  for (const f of project?.files || []) fn(f);
  walk(project?.folders);
}

/** Fingerprints of a project as loaded or saved — sent as `_base`. */
export function buildBase(project) {
  if (!project || typeof project !== 'object') return null;
  const keys = {};
  for (const k of Object.keys(project)) {
    if (!NOT_SETTINGS.has(k)) keys[k] = fingerprint(project[k]);
  }
  const hotspots = {};
  if (project.hotspots && typeof project.hotspots === 'object') {
    for (const fid of Object.keys(project.hotspots)) hotspots[fid] = fingerprint(project.hotspots[fid]);
  }
  // Which link ids this client HELD per document: a save that changes a
  // document's links then deletes only links it had (and removed), never
  // ones added since by someone else or a detection run.
  const hotspotIds = {};
  if (project.hotspots && typeof project.hotspots === 'object') {
    for (const fid of Object.keys(project.hotspots)) {
      const list = Array.isArray(project.hotspots[fid]) ? project.hotspots[fid] : [];
      hotspotIds[fid] = list.map(h => h && h.id).filter(Boolean);
    }
  }
  const fileProps = {};
  eachFile(project, (f) => { if (f?.id) fileProps[f.id] = fingerprint(filePropsOf(f)); });
    // The workspace's DETAILS (name, description, client, tags) as loaded, so
  // a whole-project save that never touched them cannot put back a stale
  // copy over an edit made through PATCH /details since this copy loaded.
  const details = {};
  for (const k of DETAIL_FIELDS) details[k] = fingerprint(project[k] ?? null);
  return { v: 1, keys, hotspots, hotspotIds, fileProps, details };
}
